import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  inject,
  signal
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

export interface SolverConfiguration {
  operationalCostWeight: number;
  distanceTravelWeight: number;
  timeSpentWeight: number;
  considerTruckLoad: boolean;
  includeWaitTime: boolean;
}

interface Point {
  x: number;
  y: number;
}

@Component({
  selector: 'app-form',
  imports: [ReactiveFormsModule],
  templateUrl: './form.component.html',
  styles: ``
})
export class FormComponent implements AfterViewInit, OnDestroy {
  @ViewChild('triangleSvg', { static: false }) triangleSvg!: ElementRef<SVGElement>;

  private fb = inject(FormBuilder);

  solverForm: FormGroup;
  totalWeight = signal(0);
  isDragging = signal(false);

  // Triangle vertices (equilateral triangle)
  private readonly triangleSize = 200;
  private readonly centerX = 250;
  private readonly centerY = 200;

  // Calculate equilateral triangle vertices
  vertices = {
    operational: { x: this.centerX, y: this.centerY - this.triangleSize * 0.866 }, // Top
    distance: {
      x: this.centerX - this.triangleSize,
      y: this.centerY + this.triangleSize * 0.5
    }, // Bottom left
    time: { x: this.centerX + this.triangleSize, y: this.centerY + this.triangleSize * 0.5 } // Bottom right
  };

  // Current point position
  currentPoint = signal<Point>({ x: this.centerX, y: this.centerY });

  private mouseMoveListener?: (event: MouseEvent) => void;
  private mouseUpListener?: () => void;

  constructor() {
    this.solverForm = this.fb.group({
      operationalCostWeight: [
        33,
        [Validators.required, Validators.min(0), Validators.max(100)]
      ],
      distanceTravelWeight: [
        33,
        [Validators.required, Validators.min(0), Validators.max(100)]
      ],
      timeSpentWeight: [34, [Validators.required, Validators.min(0), Validators.max(100)]],
      considerTruckLoad: [true],
      includeWaitTime: [false]
    });

    this.solverForm.valueChanges.subscribe(() => {
      this.updateTotalWeight();
    });

    this.updateTotalWeight();
  }

  ngAfterViewInit(): void {
    this.updatePointFromWeights();
  }

  ngOnDestroy(): void {
    this.removeMouseListeners();
  }

  private updateTotalWeight(): void {
    const weights = this.solverForm.value;
    const total =
      (weights.operationalCostWeight || 0) +
      (weights.distanceTravelWeight || 0) +
      (weights.timeSpentWeight || 0);
    this.totalWeight.set(total);
  }

  private updatePointFromWeights(): void {
    const weights = this.solverForm.value;
    const total =
      weights.operationalCostWeight +
      weights.distanceTravelWeight +
      weights.timeSpentWeight;

    if (total === 0) return;

    // Normalize weights to sum to 1
    const w1 = weights.operationalCostWeight / total;
    const w2 = weights.distanceTravelWeight / total;
    const w3 = weights.timeSpentWeight / total;

    // Calculate point using barycentric coordinates
    const point = {
      x:
        w1 * this.vertices.operational.x +
        w2 * this.vertices.distance.x +
        w3 * this.vertices.time.x,
      y:
        w1 * this.vertices.operational.y +
        w2 * this.vertices.distance.y +
        w3 * this.vertices.time.y
    };

    this.currentPoint.set(point);
  }

  private updateWeightsFromPoint(point: Point): void {
    // Calculate barycentric coordinates
    const weights = this.calculateBarycentricCoordinates(point);

    // Convert to percentages and round
    const operationalWeight = Math.round(weights.operational * 100);
    const distanceWeight = Math.round(weights.distance * 100);
    const timeWeight = Math.round(weights.time * 100);

    // Ensure they sum to 100
    const total = operationalWeight + distanceWeight + timeWeight;
    const adjustedWeights = { operationalWeight, distanceWeight, timeWeight };

    if (total !== 100) {
      const diff = 100 - total;
      // Adjust the largest weight
      if (operationalWeight >= distanceWeight && operationalWeight >= timeWeight) {
        adjustedWeights.operationalWeight += diff;
      } else if (distanceWeight >= timeWeight) {
        adjustedWeights.distanceWeight += diff;
      } else {
        adjustedWeights.timeWeight += diff;
      }
    }

    this.solverForm.patchValue(
      {
        operationalCostWeight: adjustedWeights.operationalWeight,
        distanceTravelWeight: adjustedWeights.distanceWeight,
        timeSpentWeight: adjustedWeights.timeWeight
      },
      { emitEvent: false }
    );
  }

  private calculateBarycentricCoordinates(point: Point): {
    operational: number;
    distance: number;
    time: number;
  } {
    const { operational, distance, time } = this.vertices;

    // Calculate area of the main triangle
    const mainArea =
      Math.abs(
        (distance.x - operational.x) * (time.y - operational.y) -
          (time.x - operational.x) * (distance.y - operational.y)
      ) / 2;

    // Calculate areas of sub-triangles
    const area1 =
      Math.abs(
        (distance.x - point.x) * (time.y - point.y) -
          (time.x - point.x) * (distance.y - point.y)
      ) / 2;
    const area2 =
      Math.abs(
        (operational.x - point.x) * (time.y - point.y) -
          (time.x - point.x) * (operational.y - point.y)
      ) / 2;
    const area3 =
      Math.abs(
        (operational.x - point.x) * (distance.y - point.y) -
          (distance.x - point.x) * (operational.y - point.y)
      ) / 2;

    // Barycentric coordinates
    const w1 = area1 / mainArea;
    const w2 = area2 / mainArea;
    const w3 = area3 / mainArea;

    return { operational: w1, distance: w2, time: w3 };
  }

  private isPointInTriangle(point: Point): boolean {
    const weights = this.calculateBarycentricCoordinates(point);
    return weights.operational >= 0 && weights.distance >= 0 && weights.time >= 0;
  }

  private constrainPointToTriangle(point: Point): Point {
    if (this.isPointInTriangle(point)) {
      return point;
    }

    // Find closest point on triangle edge
    const edges = [
      { start: this.vertices.operational, end: this.vertices.distance },
      { start: this.vertices.distance, end: this.vertices.time },
      { start: this.vertices.time, end: this.vertices.operational }
    ];

    let closestPoint = point;
    let minDistance = Infinity;

    edges.forEach((edge) => {
      const closest = this.closestPointOnLine(point, edge.start, edge.end);
      const distance = Math.sqrt(
        Math.pow(closest.x - point.x, 2) + Math.pow(closest.y - point.y, 2)
      );

      if (distance < minDistance) {
        minDistance = distance;
        closestPoint = closest;
      }
    });

    return closestPoint;
  }

  private closestPointOnLine(point: Point, lineStart: Point, lineEnd: Point): Point {
    const A = point.x - lineStart.x;
    const B = point.y - lineStart.y;
    const C = lineEnd.x - lineStart.x;
    const D = lineEnd.y - lineStart.y;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;

    let param = -1;
    if (lenSq !== 0) {
      param = dot / lenSq;
    }

    let xx, yy;

    if (param < 0) {
      xx = lineStart.x;
      yy = lineStart.y;
    } else if (param > 1) {
      xx = lineEnd.x;
      yy = lineEnd.y;
    } else {
      xx = lineStart.x + param * C;
      yy = lineStart.y + param * D;
    }

    return { x: xx, y: yy };
  }

  onMouseDown(event: MouseEvent): void {
    this.isDragging.set(true);
    this.addMouseListeners();
    this.updatePointFromMouse(event);
  }

  private addMouseListeners(): void {
    this.mouseMoveListener = (event: MouseEvent) => {
      if (this.isDragging()) {
        this.updatePointFromMouse(event);
      }
    };

    this.mouseUpListener = () => {
      this.isDragging.set(false);
      this.removeMouseListeners();
    };

    document.addEventListener('mousemove', this.mouseMoveListener);
    document.addEventListener('mouseup', this.mouseUpListener);
  }

  private removeMouseListeners(): void {
    if (this.mouseMoveListener) {
      document.removeEventListener('mousemove', this.mouseMoveListener);
    }
    if (this.mouseUpListener) {
      document.removeEventListener('mouseup', this.mouseUpListener);
    }
  }

  private updatePointFromMouse(event: MouseEvent): void {
    const svg = this.triangleSvg.nativeElement;
    const rect = svg.getBoundingClientRect();

    const mousePoint = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };

    const constrainedPoint = this.constrainPointToTriangle(mousePoint);
    this.currentPoint.set(constrainedPoint);
    this.updateWeightsFromPoint(constrainedPoint);
  }

  get isWeightValid(): boolean {
    return this.totalWeight() === 100;
  }

  onSubmit(): void {
    if (this.solverForm.valid && this.isWeightValid) {
      const config: SolverConfiguration = this.solverForm.value;
      console.log('Solver Configuration:', config);
    }
  }

  resetWeights(): void {
    this.solverForm.patchValue({
      operationalCostWeight: 33,
      distanceTravelWeight: 33,
      timeSpentWeight: 34
    });
    this.updatePointFromWeights();
  }
}
